import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode,
} from 'firebase/auth';
import { getAuthDeferred } from '@/utils/firebase';
import { useAuth } from '@/lib/auth';
import { prefetchDashboard } from '@/lib/prefetch-dashboard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, KeyRound } from 'lucide-react';
import { GreetLogo } from '@/components/brand/GreetLogo';

const actionSearchSchema = z.object({
  mode: z.enum(['verifyEmail', 'resetPassword', 'recoverEmail']),
  oobCode: z.string(),
  continueUrl: z.string().optional(),
  lang: z.string().optional(),
});

export const Route = createFileRoute('/auth-action')({
  component: AuthActionPage,
  validateSearch: zodValidator(actionSearchSchema),
});

function AuthActionPage() {
  const { mode, oobCode, continueUrl } = Route.useSearch();

  switch (mode) {
    case 'verifyEmail':
      return <VerifyEmailHandler oobCode={oobCode} continueUrl={continueUrl} />;
    case 'resetPassword':
      return <ResetPasswordHandler oobCode={oobCode} />;
    case 'recoverEmail':
      return <RecoverEmailHandler oobCode={oobCode} />;
    default:
      return (
        <ActionLayout>
          <StatusMessage
            icon={<XCircle className="h-8 w-8 text-red-500" />}
            title="Invalid action"
            description="The link you followed is invalid."
          />
          <ContinueButton to="/" label="Go to login" />
        </ActionLayout>
      );
  }
}

function VerifyEmailHandler({
  oobCode,
  continueUrl,
}: {
  oobCode: string;
  continueUrl?: string;
}) {
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const navigate = useNavigate();
  const { getUser, setUser } = useAuth();
  const auth = getAuthDeferred();

  useEffect(() => {
    (async () => {
      try {
        await applyActionCode(auth, oobCode);

        // Refresh token so backend sees updated email_verified
        if (auth.currentUser) {
          await auth.currentUser.reload();
          const token = await auth.currentUser.getIdToken(true);
          const userDetails = await getUser(token);
          if (userDetails) {
            setUser(userDetails);
          }
        }

        // Redirect immediately
        if (continueUrl) {
          window.location.href = continueUrl;
        } else if (auth.currentUser) {
          await prefetchDashboard();
          navigate({ to: '/dashboard' });
        } else {
          navigate({ to: '/' });
        }
      } catch {
        setStatus('error');
      }
    })();
  }, [oobCode]);

  if (status === 'error') {
    return (
      <ActionLayout>
        <StatusMessage
          icon={<XCircle className="h-8 w-8 text-red-500" />}
          title="Verification failed"
          description="This link is invalid or has expired. Please request a new verification email."
        />
        <ContinueButton to="/verify-email" label="Back to verification" />
      </ActionLayout>
    );
  }

  return <LoadingState message="Verifying your email..." />;
}

const newPasswordSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type NewPasswordFormValues = z.infer<typeof newPasswordSchema>;

function ResetPasswordHandler({
  oobCode,
}: {
  oobCode: string;
}) {
  const [status, setStatus] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const auth = getAuthDeferred();

  const form = useForm<NewPasswordFormValues>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  useEffect(() => {
    (async () => {
      try {
        const accountEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(accountEmail);
        setStatus('form');
      } catch {
        setStatus('error');
      }
    })();
  }, [oobCode]);

  const onSubmit = async (values: NewPasswordFormValues) => {
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, values.password);
      setStatus('success');
      toast.success('Password has been reset');
    } catch {
      toast.error('Failed to reset password. The link may have expired.');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return <LoadingState message="Verifying reset link..." />;
  }

  if (status === 'error') {
    return (
      <ActionLayout>
        <StatusMessage
          icon={<XCircle className="h-8 w-8 text-red-500" />}
          title="Invalid reset link"
          description="This link is invalid or has expired. Please request a new password reset."
        />
        <ContinueButton to="/" label="Go to login" />
      </ActionLayout>
    );
  }

  if (status === 'success') {
    return (
      <ActionLayout>
        <StatusMessage
          icon={<CheckCircle className="h-8 w-8 text-green-500" />}
          title="Password reset"
          description="Your password has been updated."
        />
        <ContinueButton to="/" label="Continue to login" />
      </ActionLayout>
    );
  }

  return (
    <ActionLayout>
      <div className="flex justify-center mb-6">
        <div className="rounded-sm bg-blue-50 dark:bg-blue-900/20 p-4">
          <KeyRound className="h-8 w-8 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
      <h1 className="text-xl font-semibold mb-5">Reset your password</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 text-left">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="Enter new password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <Input type="password" placeholder="Confirm new password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Resetting...' : 'Reset password'}
          </Button>
        </form>
      </Form>
    </ActionLayout>
  );
}

function RecoverEmailHandler({ oobCode }: { oobCode: string }) {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [restoredEmail, setRestoredEmail] = useState('');
  const auth = getAuthDeferred();

  useEffect(() => {
    (async () => {
      try {
        const info = await checkActionCode(auth, oobCode);
        const email = info.data.email;
        if (email) setRestoredEmail(email);
        await applyActionCode(auth, oobCode);
        setStatus('success');
      } catch {
        setStatus('error');
      }
    })();
  }, [oobCode]);

  if (status === 'loading') {
    return <LoadingState message="Recovering your email..." />;
  }

  if (status === 'error') {
    return (
      <ActionLayout>
        <StatusMessage
          icon={<XCircle className="h-8 w-8 text-red-500" />}
          title="Recovery failed"
          description="This link is invalid or has expired."
        />
        <ContinueButton to="/" label="Go to login" />
      </ActionLayout>
    );
  }

  return (
    <ActionLayout>
      <StatusMessage
        icon={<CheckCircle className="h-8 w-8 text-green-500" />}
        title="Email restored"
        description={
          restoredEmail
            ? `Your email has been reverted to ${restoredEmail}.`
            : 'Your email has been restored.'
        }
      />
      <ContinueButton to="/" label="Go to login" />
    </ActionLayout>
  );
}

function ActionLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950 p-6">
      <div className="w-full max-w-sm text-center">
        <GreetLogo className="h-8 w-auto mb-8 mx-auto" />
        {children}
      </div>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <ActionLayout>
      <div className="flex justify-center mb-6">
        <div className="size-10 rounded-full border-4 border-gray-200 border-t-foreground animate-spin" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </ActionLayout>
  );
}

function StatusMessage({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <>
      <div className="flex justify-center mb-6">{icon}</div>
      <h1 className="text-xl font-semibold mb-2">{title}</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </>
  );
}

function ContinueButton({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate();
  return (
    <Button
      variant="outline"
      className="w-full mt-6"
      onClick={() => navigate({ to: to as any })}
    >
      {label}
    </Button>
  );
}
